import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';
import type { RefusalReasonCode } from './interventionRequestsApi';

/**
 * The back-office bell. A technician's feed was built and removed in recette: the `pending` badge
 * his own screen carries already names the one thing he has to act on.
 *
 * Nothing here is a stored notification: every item is derived from `intervention_requests`
 * (D42), so the feed cannot disagree with the follow-up table or the mobile list about the same
 * request. The only persisted thing is the reader's watermark.
 */

/**
 * `no_answer` is the one with no event of its own — nothing happened, which is exactly the hole
 * D5 left by deleting « expirée ».
 */
export type NotificationKind = 'accepted' | 'declined' | 'no_answer';

export interface NotificationItem {
  /** `<kind>-<requestId>` — stable across refetches, unique inside one feed. */
  id: string;
  kind: NotificationKind;
  requestId: number;
  issueKey: string;
  serviceDeskId: number;
  /** `null` on an orphan row: `issues` is TRUNCATEd on a resync and the join is LEFT. */
  store: string | null;
  requestType: string | null;
  /** Who answered. */
  technicianName: string | null;
  refusalReasonCode: RefusalReasonCode | null;
  eventAt: string;
  /**
   * Seconds since the event, **measured by Postgres** — the same rule as `elapsedSeconds` on a
   * request. One clock in the system, so a browser with a wrong time cannot render « il y a 3 h »
   * on something ten minutes old.
   */
  ageSeconds: number;
  unread: boolean;
}

export interface NotificationFeed {
  items: NotificationItem[];
  unreadCount: number;
  /** `null` when the reader has never opened it. */
  seenAt: string | null;
}

const BASE = 'notifications';

export const fetchNotifications = async (): Promise<NotificationFeed> =>
  unwrap<NotificationFeed>(await fetchWithAuth(BASE), 'Failed to fetch the notifications');

/**
 * Mark everything read, up to now. Answers with the **recomputed** feed rather than a bare 204:
 * the screen zeroes its badge on the answer, so a failed call leaves the unread state where it
 * was instead of losing it in silence.
 */
export const markNotificationsSeen = async (): Promise<NotificationFeed> =>
  unwrap<NotificationFeed>(
    await fetchWithAuth(`${BASE}/read`, { method: 'PATCH' }),
    'Failed to mark the notifications as read'
  );
